import type { Aabb2, Point2 } from './point';

/** Bounds of a point run, or `null` when there are no points. */
export const polylineBounds = (points: readonly Point2[]): Aabb2 | null => {
  if (points.length === 0) {
    return null;
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let index = 0; index < points.length; index += 1) {
    const [x, y] = points[index];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }

  return Number.isFinite(minX) ? [minX, minY, maxX, maxY] : null;
};

/** Total run length along a polyline. */
export const polylineLength = (points: readonly Point2[]): number => {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const [ax, ay] = points[index - 1];
    const [bx, by] = points[index];
    total += Math.hypot(bx - ax, by - ay);
  }
  return total;
};

/**
 * Twice the signed area of the polygon the points enclose (the shoelace sum).
 *
 * Signed, so the sign reports winding: positive is counter-clockwise in a y-up
 * space. Callers wanting an area take half the absolute value; callers wanting to
 * know inside-ness of nested loops want the sign.
 *
 * The run is treated as implicitly closed — the last point is joined back to the
 * first — so it is meaningful whether or not the caller repeated the start point.
 */
export const shoelaceDouble = (points: readonly Point2[]): number => {
  if (points.length < 3) {
    return 0;
  }
  let sum = 0;
  for (let index = 0; index < points.length; index += 1) {
    const [ax, ay] = points[index];
    const [bx, by] = points[(index + 1) % points.length];
    sum += ax * by - bx * ay;
  }
  return sum;
};

/** Area enclosed by a closed point run. Always non-negative. */
export const polygonArea = (points: readonly Point2[]): number =>
  Math.abs(shoelaceDouble(points)) / 2;

/** Squared distance from a point to a segment, and where along it the foot lies. */
export const pointToSegmentDistanceSq = (
  point: Point2,
  a: Point2,
  b: Point2,
): number => {
  const [px, py] = point;
  const [ax, ay] = a;
  const [bx, by] = b;

  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    // Degenerate segment: the two endpoints coincide, so it is just a point.
    return (px - ax) ** 2 + (py - ay) ** 2;
  }

  // Projection parameter, clamped to the segment so the foot never runs past an
  // endpoint — the difference between distance-to-segment and distance-to-line.
  const t = Math.max(
    0,
    Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSq),
  );
  const footX = ax + t * dx;
  const footY = ay + t * dy;

  return (px - footX) ** 2 + (py - footY) ** 2;
};

/**
 * The point on segment `a`–`b` nearest to `point`.
 *
 * Clamped to the segment, so the result is always on the drawn line rather than
 * on its infinite extension — which is what makes this usable as a snap target.
 */
export const closestPointOnSegment = (
  point: Point2,
  a: Point2,
  b: Point2,
): Point2 => {
  const [px, py] = point;
  const [ax, ay] = a;
  const [bx, by] = b;

  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) {
    return a;
  }

  const t = Math.max(
    0,
    Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSq),
  );
  return [ax + t * dx, ay + t * dy];
};

/** Squared distance from a point to the nearest part of a polyline. */
export const pointToPolylineDistanceSq = (
  point: Point2,
  points: readonly Point2[],
): number => {
  if (points.length === 0) {
    return Infinity;
  }
  if (points.length === 1) {
    return (point[0] - points[0][0]) ** 2 + (point[1] - points[0][1]) ** 2;
  }

  let best = Infinity;
  for (let index = 1; index < points.length; index += 1) {
    const distanceSq = pointToSegmentDistanceSq(
      point,
      points[index - 1],
      points[index],
    );
    if (distanceSq < best) {
      best = distanceSq;
    }
  }
  return best;
};
